/**
 * Client-side cache helpers (localStorage) for AI pipeline results.
 *
 * Fingerprinting rules:
 * - analyze / translate: depend on CV only (versionId + resumeHash)
 * - compare / optimize / generate / coverLetter: depend on CV + job (versionId + resumeHash + jobHash)
 *
 * NOTE: hashString is a 32-bit non-cryptographic hash kept in sync with the
 * server-side helper in api/lib/ai-pipeline-cache.ts. Phase 2 will replace
 * this with SHA-256 via crypto.subtle on both sides.
 */

export type CvFingerprint = { versionId: string; resumeHash: string };
export type CvJobFingerprint = CvFingerprint & { jobHash: string };

export function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

export function createCvFingerprint(versionId: string, resumeText: string): CvFingerprint {
  return { versionId, resumeHash: hashString(resumeText) };
}

export function createCvJobFingerprint(
  versionId: string,
  resumeText: string,
  jobDescription: string,
): CvJobFingerprint {
  return {
    versionId,
    resumeHash: hashString(resumeText),
    jobHash: hashString(jobDescription.trim()),
  };
}

export function getCachedStep<T>(
  cacheKey: string,
  fpKey: string,
  fp: unknown,
): { data: T; cached: true } | null {
  try {
    const storedFp = localStorage.getItem(fpKey);
    const storedData = localStorage.getItem(cacheKey);
    if (!storedFp || !storedData) return null;
    if (storedFp !== JSON.stringify(fp)) return null;
    return { data: JSON.parse(storedData) as T, cached: true };
  } catch {
    return null;
  }
}

export function setCachedStep<T>(
  cacheKey: string,
  fpKey: string,
  fp: unknown,
  data: T,
): void {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
    localStorage.setItem(fpKey, JSON.stringify(fp));
  } catch (e) {
    console.warn('Failed to cache result:', e);
    // Fire quota toast asynchronously if this is a quota error
    if (
      e instanceof Error &&
      (e.name === 'QuotaExceededError' || (e as unknown as { code?: number }).code === 22)
    ) {
      void import('./safe-storage').then(({ warnOnQuotaOnce }) =>
        warnOnQuotaOnce({ ok: false, error: 'quota' }),
      );
    }
  }
}

/** Safely read & parse a JSON-serialized value from localStorage. */
export function loadStoredJson<T>(key: string): T | null {
  try {
    const s = localStorage.getItem(key);
    if (!s) return null;
    const parsed = JSON.parse(s) as T;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    console.error('loadStoredJson', key, e);
    return null;
  }
}
